import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const budgets = ["Under $500", "$500 – $1k", "$1k – $2k", "$2k+"];

const inputCls =
  "w-full bg-transparent border-0 border-b border-border focus:border-primary outline-none py-3 text-base placeholder:text-muted-foreground/60 transition-colors duration-300 rounded-none";

const initialForm = { name: "", email: "", company: "", budget: budgets[1], message: "", website: "" };

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. We reply within one business day.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong. Please email sivicbusinessdesign@gmail.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-36 bg-white border-t border-border" data-testid="contact-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
        <Reveal className="md:col-span-5">
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-muted-foreground font-medium mb-4">
            Contact
          </p>
          <h2 className="font-syne font-bold tracking-tight text-4xl sm:text-5xl mb-8">
            Tell us about<br />your project
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-light leading-relaxed max-w-md mb-12">
            One email, one call, one honest quote. No discovery-call theatre,
            no 40-page proposals.
          </p>
          <div className="space-y-4 text-base">
            <p>
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground block mb-1">Email</span>
              <a href="mailto:sivicbusinessdesign@gmail.com" data-testid="contact-email-link" className="font-medium hover:text-primary transition-colors duration-300">
                sivicbusinessdesign@gmail.com
              </a>
            </p>
            <p>
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground block mb-1">Response time</span>
              Within one business day
            </p>
          </div>
        </Reveal>
        <Reveal className="md:col-span-7" delay={0.15}>
          <form onSubmit={submit} className="space-y-10" data-testid="enquiry-form">
            <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden opacity-0">
              <label htmlFor="enquiry-website">Website</label>
              <input
                id="enquiry-website"
                data-testid="enquiry-honeypot-input"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={set("website")}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
              <div>
                <label htmlFor="enquiry-name" className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
                  Name *
                </label>
                <input
                  id="enquiry-name"
                  data-testid="enquiry-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Jane Cooper"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="enquiry-email" className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
                  Email *
                </label>
                <input
                  id="enquiry-email"
                  data-testid="enquiry-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="jane@company.com"
                  className={inputCls}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
              <div>
                <label htmlFor="enquiry-company" className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
                  Company
                </label>
                <input
                  id="enquiry-company"
                  data-testid="enquiry-company-input"
                  value={form.company}
                  onChange={set("company")}
                  placeholder="Acme Inc."
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="enquiry-budget" className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
                  Budget
                </label>
                <select
                  id="enquiry-budget"
                  data-testid="enquiry-budget-select"
                  value={form.budget}
                  onChange={set("budget")}
                  className={`${inputCls} cursor-pointer`}
                >
                  {budgets.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="enquiry-message" className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
                Project details *
              </label>
              <textarea
                id="enquiry-message"
                data-testid="enquiry-message-input"
                required
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="What are you building, and what should it achieve?"
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit-button"
              className="inline-flex items-center gap-2 bg-foreground text-white px-8 py-4 text-sm font-medium hover:bg-primary transition-colors duration-300 active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none"
            >
              {sending ? (
                <>Sending <Loader2 size={16} className="animate-spin" /></>
              ) : (
                <>Send enquiry <ArrowUpRight size={16} /></>
              )}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
